import "./InventoryManagementBoard.css";

import { TooltipItem } from "chart.js";

import { LogoutDarkIcon } from "../../../../assets";
import { ColorConstants } from "../../../../constants";
import { DeveloperTemplate, UnitTransactionsPanel } from "../../../../templates";
import { formatNumber } from "../../../../utils";
import {
  Column,
  CursorDataGrid,
  InventorySelector,
  InvestmentKPI,
  LineChart,
  MultiSelect,
  Row,
  Select,
  StackedBarChart,
  UserHeader,
} from "../../../../widget";
import { InventoryChartKey } from "./components";
import {
  INVENTORY_STATUS_SELECT_DATA,
  inventoryTooltipLabel,
  useInventoryManagementBoard,
} from "./useInventoryManagementBoard";

const InventoryManagementBoard = (): JSX.Element => {
  const {
    hasEnoughDataForScrolling,
    gridOnChange,
    isLoading,
    dropDownData,
    selectedUnitMeasurementData,
    selectedStatuses,
    handleStatusFilter,
    activeSelectOptionText,
    setActiveSelectOptionText,
    pageData,
    kpiData,
    columns,
    onTagsFilter,
    initTagsFilterData,
    tagsFilterData,
    tagsFilterSelectedData,
    hasUnits,
    prefix,
    inventorySelectorButtons,
    handleUpdateSelectedUnitMeasurement,
    searchIssuanceTransactionsSortingCriteria,
    searchIssuanceTransactionsColumns,
    isSalesOverTimeSelected,
    unitTransactionsPanelFilter,
    handleCloseUnitTransactionsPanel,
    refresh,
    handleRefresh,
    filterExportColumns,
    filterExportRows,
  } = useInventoryManagementBoard();
  return (
    <DeveloperTemplate activeElement="Inventory">
      <div className="InventoryManagementBoard">
        <Row spacingV="ml">
          <Column span={12}>
            <UserHeader />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <div className="HeaderContainer">
              <h1>{selectedUnitMeasurementData === "" ? "" : `${selectedUnitMeasurementData} `}Inventory</h1>
              {!isLoading && (
                <div className="HeaderButtons">
                  <h3>Filter</h3>
                  <Select
                    onChange={(v) => {
                      handleUpdateSelectedUnitMeasurement(v);
                    }}
                    data={dropDownData}
                    value={selectedUnitMeasurementData}
                    className="HeaderSelect"
                  />
                  <MultiSelect
                    onChange={handleStatusFilter}
                    data={INVENTORY_STATUS_SELECT_DATA}
                    values={selectedStatuses}
                    className="HeaderSelect"
                  />
                </div>
              )}
            </div>
          </Column>
        </Row>
        <p className="body1 inventory-description">
          This page shows your organisation-wide inventory. You can switch the display by unit type, review and edit
          issued data, and track availability, reservations, and sales across all projects. Inventory can also be
          managed directly within the individual project listings.
        </p>
        <Row spacingV="ml">
          <Column span={4}>
            <InvestmentKPI amount={kpiData.totalAvailableToSell} title="Available to sell" />
          </Column>
          <Column span={4}>
            <InvestmentKPI amount={kpiData.totalReserved} title="Reserved" />
          </Column>
          <Column span={4}>
            <InvestmentKPI amount={kpiData.totalSold} title="Sold" />
          </Column>
        </Row>
        <div className="InventoryDisplayData">
          <div className="HeaderContainer">
            <h2>{activeSelectOptionText}</h2>
            <InventorySelector
              buttons={inventorySelectorButtons}
              activeSelector={activeSelectOptionText}
              onChange={setActiveSelectOptionText}
            />
          </div>
          <Row spacingV="s">
            <Column span={12}>
              <div className="InventoryChartContainer">
                {isSalesOverTimeSelected && pageData.chartDatasets && !isLoading ? (
                  <LineChart
                    height={400}
                    labels={pageData.chartLabels as string[]}
                    datasets={pageData.chartDatasets}
                    customChartColours={[ColorConstants.GREEN_500_RGB, ColorConstants.INDIGO_500_RGB]}
                    xAxisTickOffset={-26}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    yAxisTickCallbackFunction={(val: any) => {
                      return `£${formatNumber(val)}`;
                    }}
                    labelCallbackFunction={(context: TooltipItem<"line">) => {
                      const x = context.label ?? (context.parsed && context.parsed.x);
                      const y = context.parsed && context.parsed.y;
                      return `${String(x)} - £${Number(y).toLocaleString()}`;
                    }}
                  />
                ) : (
                  <StackedBarChart
                    xLabels={pageData.chartLabels}
                    data={pageData.chartData ?? []}
                    hasYAxisGridLines
                    hasYAxisTicks
                    showLegend={false}
                    customTooltipLabel={(context) => inventoryTooltipLabel(context, prefix)}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    yAxisTickCallbackFunction={(val: any) => {
                      return `${prefix ?? ""}${formatNumber(val)}`;
                    }}
                  />
                )}
                {activeSelectOptionText !== "Revenue per project" && (
                  <InventoryChartKey
                    unitMeasurement={selectedUnitMeasurementData}
                    isSalesOverTime={isSalesOverTimeSelected ?? false}
                  />
                )}
              </div>
            </Column>
          </Row>
          <div className={`InventoryFilters ${hasUnits ? "" : "NoData"}`}>
            <h3>Filter by</h3>
            <MultiSelect
              label=" "
              className="TagsFilter"
              placeholder="Tags"
              data={isSalesOverTimeSelected ? initTagsFilterData : tagsFilterData}
              onChange={(keys) => onTagsFilter(keys)}
            />
          </div>
          <Row spacingV={hasEnoughDataForScrolling ? "3xl" : undefined}>
            <Column span={12}>
              <div className={`InventoryDataGrid ${hasEnoughDataForScrolling ? "Scrollable" : ""}`}>
                <CursorDataGrid
                  key={
                    selectedUnitMeasurementData +
                    activeSelectOptionText +
                    tagsFilterSelectedData +
                    selectedStatuses.join(",")
                  }
                  onChange={gridOnChange}
                  columns={isSalesOverTimeSelected ? searchIssuanceTransactionsColumns : columns}
                  clientSide={false}
                  filterable
                  pagination={{ pageSize: 1000 }}
                  filterPosition="inventory"
                  dataIsLoading={isLoading}
                  loadingMessage=""
                  data={[]}
                  noDataMessage="No inventory data to display"
                  exportable
                  exportButtonText="Export table"
                  exportButtonIcon={<LogoutDarkIcon className="ExportButtonIcon" />}
                  exportFileName=""
                  filterExportColumns={filterExportColumns}
                  filterExportRows={filterExportRows}
                  infiniteScroll
                  hasTotalRow={!isSalesOverTimeSelected}
                  totalColumnNames={
                    isSalesOverTimeSelected ? undefined : ["availableUnits", "reservedUnits", "soldUnits"]
                  }
                  defaultSortingCriteria={
                    isSalesOverTimeSelected ? searchIssuanceTransactionsSortingCriteria : undefined
                  }
                  refresh={refresh}
                />
              </div>
            </Column>
          </Row>
        </div>
      </div>
      <UnitTransactionsPanel
        filter={unitTransactionsPanelFilter}
        show={unitTransactionsPanelFilter !== undefined}
        onClose={handleCloseUnitTransactionsPanel}
        handleRefresh={handleRefresh}
      />
    </DeveloperTemplate>
  );
};

export default InventoryManagementBoard;
